import React, { useState, useEffect } from 'react';
import styles from './Confirmation.module.css';
import { ProductsCatalog } from '@services/products-catalog';
import { StorageService } from '@services/storage-service';
import { UrlEncoderService } from '@services/url-encoder-service';
import Papa from 'papaparse';
import type { ProductInfo } from '@services/products-catalog';
import type { ProductRecommendation, ClientInfo, Answer, Question } from '@/types';

interface ConfirmationProps {
  clientInfo: ClientInfo;
  products: ProductRecommendation[];
  bonusProducts: string[];
  totalPoints: number;
  redirectUrl: string;
  answers: Map<string, Answer>;
  questions: Question[];
  onRedirect?: () => void;
}

interface GroupedProduct {
  productInfo: ProductInfo;
  questions: string[];
}

export const Confirmation: React.FC<ConfirmationProps> = ({
  clientInfo,
  products,
  bonusProducts,
  totalPoints: _totalPoints,
  redirectUrl: _redirectUrl,
  answers,
  questions,
  onRedirect: _onRedirect,
}) => {
  const [featuredProducts, setFeaturedProducts] = useState<ProductInfo[]>([]);
  const [bestsellerData, setBestsellerData] = useState<Map<string, number>>(new Map());

  // Save results and generate shareable link
  useEffect(() => {
    try {
      // Save to localStorage for "previous results" feature
      StorageService.saveResults(
        clientInfo,
        products,
        bonusProducts.map(code => ({
          code,
          name: code.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          count: 1,
          reason: ['bonus']
        })),
        answers,
        questions
      );
      
      // Generate compact shareable link with encoded data
      const shareableUrl = UrlEncoderService.generateShareableUrl(answers, clientInfo, questions);
      console.log('Shareable link generated:', shareableUrl);
    } catch (error) {
      console.error('Error saving results:', error);
    }
  }, [answers, products, bonusProducts, clientInfo, questions]);

  // Load bestseller data
  useEffect(() => {
    const loadBestsellerData = async () => {
      try {
        const response = await fetch('https://pemlab.garciathomas.workers.dev/products');
        const data = await response.json();
        
        const bestsellers = new Map<string, number>();
        data.forEach((product: any) => {
          if (product.bestseller) {
            bestsellers.set(product.handle, parseInt(product.bestseller));
          }
        });
        
        setBestsellerData(bestsellers);
        console.log('Bestseller data loaded:', bestsellers);
      } catch (error) {
        console.error('Error loading bestseller data:', error);
      }
    };
    
    loadBestsellerData();
  }, []);

  // Load product information and CSV data
  useEffect(() => {
    const loadData = async () => {
      // Combine all product handles (regular + bonus)
      const allProductHandles = [...products.map(p => p.code), ...bonusProducts];
      
      // Load products catalog
      await ProductsCatalog.loadProducts();
      const info = ProductsCatalog.getProductsInfo(allProductHandles);
      
      // Load CSV data to get texte_brique
      try {
        const response = await fetch('https://docs.google.com/spreadsheets/d/e/2PACX-1vR_hJ0Mu6ef08AvrSF7xbG8l5aPOJ365CU0ZvQ0cKTbgNYZ2Zdao0RsKaIPRK6IO36ZANRiRbUK5ZST/pub?gid=1709293458&single=true&output=csv');
        const csvText = await response.text();
        
        // Parse CSV properly with papaparse
        const parseResult = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true
        });
        
        const data = parseResult.data;
        
        // Group products by texte_brique
        const grouped = new Map<string, GroupedProduct[]>();
        
        console.log('All product handles:', allProductHandles);
        console.log('Products info loaded:', info);
        
        // For each product in our recommendations, find its texte_brique
        allProductHandles.forEach(productHandle => {
          // Find all rows that contain this product
          const productRows = data.filter((row: any) => {
            const productsInRow = row.produits_concaténés?.split('|').map((p: string) => p.trim()) || [];
            return productsInRow.includes(productHandle);
          });
          
          console.log(`Product ${productHandle} found in ${productRows.length} rows`);
          
          productRows.forEach((row: any) => {
            // Get texte_brique or fallback to Question
            const texteBrique = row.texte_brique?.trim() || row.Question || 'Autres produits';
            
            // Find the product info
            const productInfo = info.find(p => p.handle === productHandle);
            if (!productInfo) {
              console.warn(`Product info not found for ${productHandle}`);
              return;
            }
            
            // Add to grouped products
            if (!grouped.has(texteBrique)) {
              grouped.set(texteBrique, []);
            }
            
            // Check if product already exists in this group
            const existingProduct = grouped.get(texteBrique)!.find(p => p.productInfo.handle === productInfo.handle);
            if (!existingProduct) {
              grouped.get(texteBrique)!.push({
                productInfo,
                questions: [row.Question]
              });
            }
          });
        });
        
        // Add bonus products to a special group if they're not already grouped
        if (bonusProducts.length > 0) {
          const bonusGroup: GroupedProduct[] = [];
          bonusProducts.forEach(productCode => {
            const productInfo = info.find(p => p.handle === productCode);
            if (productInfo) {
              // Check if this bonus product is already in another group
              let alreadyGrouped = false;
              grouped.forEach(group => {
                if (group.find(p => p.productInfo.handle === productCode)) {
                  alreadyGrouped = true;
                }
              });
              
              if (!alreadyGrouped) {
                bonusGroup.push({
                  productInfo,
                  questions: ['Produits bonus']
                });
              }
            }
          });
          
          if (bonusGroup.length > 0) {
            grouped.set('🎁 Produits bonus', bonusGroup);
          }
        }
        
        console.log('Grouped products:', grouped);
        
        // Calculate featured products
        const featured: ProductInfo[] = [];
        const productFrequency = new Map<string, number>();
        
        // Count product frequency
        allProductHandles.forEach(handle => {
          productFrequency.set(handle, (productFrequency.get(handle) || 0) + 1);
        });
        
        // 1. First add bonus products
        bonusProducts.forEach(handle => {
          const productInfo = info.find(p => p.handle === handle);
          if (productInfo && featured.length < 5) {
            featured.push(productInfo);
          }
        });
        
        // 2. If less than 5, add most frequent products
        if (featured.length < 5) {
          const sortedByFrequency = Array.from(productFrequency.entries())
            .filter(([handle]) => !bonusProducts.includes(handle)) // Exclude already added bonus products
            .filter(([handle]) => allProductHandles.includes(handle)) // Only products that are in final recommendations
            .sort((a, b) => b[1] - a[1]); // Sort by frequency descending
          
          for (const [handle] of sortedByFrequency) {
            if (featured.length >= 5) break;
            const productInfo = info.find(p => p.handle === handle);
            if (productInfo && !featured.find(p => p.handle === handle)) {
              featured.push(productInfo);
            }
          }
        }
        
        // 3. If still less than 5, add bestsellers
        if (featured.length < 5 && bestsellerData.size > 0) {
          // First add bestsellers with score 1 (only from final recommendations)
          const bestsellers1 = allProductHandles
            .filter(handle => bestsellerData.get(handle) === 1)
            .filter(handle => !featured.find(p => p.handle === handle));
          
          for (const handle of bestsellers1) {
            if (featured.length >= 5) break;
            const productInfo = info.find(p => p.handle === handle);
            if (productInfo) {
              featured.push(productInfo);
            }
          }
          
          // If still less than 5, add bestsellers with score 2 (only from final recommendations)
          if (featured.length < 5) {
            const bestsellers2 = allProductHandles
              .filter(handle => bestsellerData.get(handle) === 2)
              .filter(handle => !featured.find(p => p.handle === handle));
            
            for (const handle of bestsellers2) {
              if (featured.length >= 5) break;
              const productInfo = info.find(p => p.handle === handle);
              if (productInfo) {
                featured.push(productInfo);
              }
            }
          }
        }
        
        setFeaturedProducts(featured);
        
      } catch (error) {
        console.error('Error loading CSV data:', error);
      }
      
    };
    loadData();
  }, [products, bonusProducts, bestsellerData]);

  // Toggle section expansion

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.introSection}>
          <div className={styles.solenePhoto}>
            <img src="/solene.png" alt="Solène, directrice bien-être chez Pemlab" />
          </div>
          <div className={styles.introMessage}>
            <h1 className={styles.greeting}>Hello {clientInfo.prenom} 👋</h1>
            <p className={styles.messageText}>
              Merci d'avoir répondu à notre bilan, j'ai bien analysé tes réponses et je te propose donc une sélection ci-dessous de 5 produits qui correspondent vraiment à tes besoins et à ta problématique.
            </p>
            <p className={styles.signature}>
              <strong>Solenne</strong>, Directrice bien-être chez Pemlab
            </p>
          </div>
        </div>

        {/* Featured Products Section */}
        {featuredProducts.length > 0 && (
          <div className={styles.featuredSection}>
            <h2 className={styles.sectionTitle}>
              ⭐ Mes 5 essentiels
            </h2>
            <div className={styles.featuredGrid}>
              {featuredProducts.slice(0, 5).map((productInfo) => (
                <a
                  key={productInfo.handle}
                  href={productInfo.productUrl}
                  className={styles.featuredCard}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {productInfo.imageUrl && (
                    <div className={styles.featuredImage}>
                      <img src={productInfo.imageUrl} alt={productInfo.title} />
                    </div>
                  )}
                  <h3 className={styles.featuredTitle}>{productInfo.title}</h3>
                  <span className={styles.featuredLink}>Découvrir →</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Consultation Section */}
        <div className={styles.consultationSection}>
          <div className={styles.consultationContent}>
            <div className={styles.consultationImage}>
              <img 
                src="/consultation-naturopathe.jpg" 
                alt="Consultation naturopathe Pemlab" 
                className={styles.consultationImg}
              />
            </div>
            <div className={styles.consultationText}>
              <h2 className={styles.consultationTitle}>
                Des questions sur ton bilan ou besoin d'en parler avec une experte ?
              </h2>
              <p className={styles.consultationSubtitle}>
                Si tu as des questions concernant ton bilan, ou si tu veux aller plus loin, tu peux prendre rendez-vous avec l'une de nos naturopathes
              </p>
              <p className={styles.consultationBenefit}>
                Alors profite de <strong>20 minutes offertes</strong> en visio ou en physique 🌱
              </p>
              <p className={styles.consultationFree}>
                C'est 100% gratuit ✅
              </p>
              <a
                href="https://pemlab-paris.fr/pages/minis-consults-pemlab"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.consultationCta}
              >
                Ma consultation offerte
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};